import React, { useEffect, useRef } from 'react';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const smoothStep = (edge0, edge1, value) => {
  const t = clamp((value - edge0) / Math.max(edge1 - edge0, 0.0001), 0, 1);
  return t * t * (3 - 2 * t);
};

const toPlainRect = (rect) => ({
  left: rect.left,
  top: rect.top,
  width: rect.width,
  height: rect.height,
});

const pickSourceStyles = (element) => {
  const computed = window.getComputedStyle(element);
  return {
    color: computed.color,
    fontFamily: computed.fontFamily,
    fontSize: computed.fontSize,
    fontWeight: computed.fontWeight,
    letterSpacing: computed.letterSpacing,
    lineHeight: computed.lineHeight,
    textAlign: computed.textAlign,
    textShadow: computed.textShadow,
    filter: computed.filter,
  };
};

const getBridgeProfile = (sourceRect, targetRect, isMobileViewport) => {
  if (isMobileViewport) {
    return {
      distanceToHeader: sourceRect.bottom - targetRect.bottom,
      startDistance: clamp(sourceRect.height * 0.72, 64, 108),
      endDistance: Math.max(6, targetRect.height * 0.16),
      sourceStillBelowHeader: sourceRect.bottom > targetRect.bottom + 10,
      sourceFadeStart: 0.14,
      sourceFadeEnd: 0.58,
      targetFadeStart: 0.76,
      targetFadeEnd: 0.985,
      overlayStart: 0.02,
      overlayPeak: 0.14,
      overlayEnd: 0.92,
      minVisibleProgress: 0.015,
    };
  }

  return {
    distanceToHeader: sourceRect.top - targetRect.bottom,
    startDistance: clamp(sourceRect.height * 0.5, 52, 128),
    endDistance: Math.max(8, targetRect.height * 0.2),
    sourceStillBelowHeader: sourceRect.top > targetRect.top + 6,
    sourceFadeStart: 0.08,
    sourceFadeEnd: 0.5,
    targetFadeStart: 0.66,
    targetFadeEnd: 0.94,
    overlayStart: 0.01,
    overlayPeak: 0.1,
    overlayEnd: 0.88,
    minVisibleProgress: 0.01,
  };
};

const LogoBridge = ({ enabled = false, sourceElement, targetElement }) => {
  const overlayRef = useRef(null);
  const cloneRef = useRef(null);
  const textRef = useRef(null);
  const metricsRef = useRef(null);

  useEffect(() => {
    if (!enabled || !sourceElement || !targetElement || typeof window === 'undefined') {
      if (sourceElement) {
        sourceElement.style.removeProperty('opacity');
        sourceElement.style.removeProperty('will-change');
      }
      if (targetElement) {
        targetElement.style.removeProperty('opacity');
        targetElement.style.removeProperty('will-change');
      }
      if (overlayRef.current) {
        overlayRef.current.style.opacity = '0';
      }
      return undefined;
    }

    let rafId = null;
    let disposed = false;
    let lastVisible = false;
    let lastFrameKey = '';
    let lastSourceOpacity = '';
    let lastTargetOpacity = '';

    const measure = () => {
      const sourceRect = sourceElement.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();
      const isMobileViewport = window.innerWidth < 768;

      if (sourceRect.width === 0 || sourceRect.height === 0 || targetRect.width === 0 || targetRect.height === 0) {
        metricsRef.current = null;
        return;
      }

      metricsRef.current = {
        isMobileViewport,
        sourceDocLeft: sourceRect.left + window.scrollX,
        sourceDocTop: sourceRect.top + window.scrollY,
        sourceWidth: sourceRect.width,
        sourceHeight: sourceRect.height,
        targetLeft: targetRect.left,
        targetTop: targetRect.top,
        targetBottom: targetRect.bottom,
        targetWidth: targetRect.width,
        targetHeight: targetRect.height,
        sourceStyles: pickSourceStyles(sourceElement),
      };
    };

    const update = () => {
      rafId = null;
      if (disposed) return;

      const overlayEl = overlayRef.current;
      const cloneEl = cloneRef.current;
      const textEl = textRef.current;
      if (!overlayEl || !cloneEl || !textEl) return;

      const viewportHeight = window.innerHeight || 0;
      const metrics = metricsRef.current;
      if (!metrics) {
        overlayEl.style.opacity = '0';
        lastVisible = false;
        return;
      }

      const sourceRect = {
        left: metrics.sourceDocLeft - window.scrollX,
        top: metrics.sourceDocTop - window.scrollY,
        width: metrics.sourceWidth,
        height: metrics.sourceHeight,
      };
      sourceRect.bottom = sourceRect.top + sourceRect.height;

      const targetRect = {
        left: metrics.targetLeft,
        top: metrics.targetTop,
        bottom: metrics.targetBottom,
        width: metrics.targetWidth,
        height: metrics.targetHeight,
      };

      const profile = getBridgeProfile(sourceRect, targetRect, metrics.isMobileViewport);
      const rawProgress = (profile.startDistance - profile.distanceToHeader) / Math.max(profile.startDistance - profile.endDistance, 1);
      const progress = clamp(rawProgress, 0, 1);

      const sourceVisible = sourceRect.bottom > 0 && sourceRect.top < viewportHeight;
      const showOverlay = sourceVisible && profile.sourceStillBelowHeader && progress > profile.minVisibleProgress && progress < 0.995;

      const sourceOpacity = metrics.isMobileViewport
        ? (showOverlay ? 0 : 1 - smoothStep(0.88, 1, progress))
        : 1 - smoothStep(profile.sourceFadeStart, profile.sourceFadeEnd, progress);
      const targetOpacity = metrics.isMobileViewport
        ? smoothStep(0.9, 0.99, progress)
        : smoothStep(profile.targetFadeStart, profile.targetFadeEnd, progress);

      const nextSourceOpacity = `${Math.round(sourceOpacity * 1000) / 1000}`;
      const nextTargetOpacity = `${Math.round(targetOpacity * 1000) / 1000}`;
      if (nextSourceOpacity !== lastSourceOpacity) {
        sourceElement.style.opacity = nextSourceOpacity;
        sourceElement.style.willChange = 'opacity';
        lastSourceOpacity = nextSourceOpacity;
      }
      if (nextTargetOpacity !== lastTargetOpacity) {
        targetElement.style.opacity = nextTargetOpacity;
        targetElement.style.willChange = 'opacity';
        lastTargetOpacity = nextTargetOpacity;
      }

      if (!showOverlay) {
        overlayEl.style.opacity = '0';
        lastVisible = false;
        lastFrameKey = '';
        return;
      }

      const eased = smoothStep(0, 1, progress);
      const x = sourceRect.left + (targetRect.left - sourceRect.left) * eased;
      const y = sourceRect.top + (targetRect.top - sourceRect.top) * eased;
      const scaleX = 1 + ((targetRect.width / sourceRect.width) - 1) * eased;
      const scaleY = 1 + ((targetRect.height / sourceRect.height) - 1) * eased;
      const overlayOpacity = smoothStep(profile.overlayStart, profile.overlayPeak, progress) * (1 - smoothStep(profile.overlayEnd, 1, progress));
      const frameKey = `${Math.round(x * 10)}|${Math.round(y * 10)}|${Math.round(scaleX * 1000)}|${Math.round(scaleY * 1000)}|${Math.round(overlayOpacity * 1000)}|${Math.round(sourceOpacity * 1000)}|${Math.round(targetOpacity * 1000)}`;

      if (!lastVisible) {
        const sourceStyles = metrics.sourceStyles;
        textEl.style.color = sourceStyles.color;
        textEl.style.filter = metrics.isMobileViewport ? 'none' : sourceStyles.filter;
        textEl.style.fontFamily = sourceStyles.fontFamily;
        textEl.style.fontSize = sourceStyles.fontSize;
        textEl.style.fontWeight = sourceStyles.fontWeight;
        textEl.style.letterSpacing = sourceStyles.letterSpacing;
        textEl.style.lineHeight = sourceStyles.lineHeight;
        textEl.style.textAlign = sourceStyles.textAlign;
        textEl.style.textShadow = metrics.isMobileViewport ? 'none' : sourceStyles.textShadow;
        textEl.style.alignItems = sourceStyles.textAlign === 'center' ? 'center' : 'flex-start';
        cloneEl.style.width = `${metrics.sourceWidth}px`;
        cloneEl.style.height = `${metrics.sourceHeight}px`;
      }

      if (frameKey !== lastFrameKey) {
        overlayEl.style.opacity = '1';
        cloneEl.style.opacity = `${overlayOpacity}`;
        cloneEl.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scaleX}, ${scaleY})`;
        lastFrameKey = frameKey;
      }
      lastVisible = true;
    };

    const schedule = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(update);
    };

    measure();
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', measure);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', measure);
    window.addEventListener('orientationchange', schedule);
    window.visualViewport?.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', schedule);

    return () => {
      disposed = true;
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', measure);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', measure);
      window.removeEventListener('orientationchange', schedule);
      window.visualViewport?.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', schedule);
      sourceElement.style.removeProperty('opacity');
      sourceElement.style.removeProperty('will-change');
      targetElement.style.removeProperty('opacity');
      targetElement.style.removeProperty('will-change');
      metricsRef.current = null;
      if (overlayRef.current) {
        overlayRef.current.style.opacity = '0';
      }
    };
  }, [enabled, sourceElement, targetElement]);

  return (
    <div
      ref={overlayRef}
      className="pointer-events-none fixed inset-0 z-[65]"
      aria-hidden="true"
      style={{ opacity: 0 }}
    >
      <div
        ref={cloneRef}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          opacity: 0,
          transform: 'translate3d(0, 0, 0) scale(1)',
          transformOrigin: 'top left',
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
        }}
      >
        <div
          ref={textRef}
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            backfaceVisibility: 'hidden',
          }}
        >
          <span style={{ display: 'block' }}>CRA</span>
          <span style={{ display: 'block' }}>PULE</span>
        </div>
      </div>
    </div>
  );
};

export default LogoBridge;
